import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiIcon } from "@miplatform/components";
import { PERMISSIONS } from "@miplatform/constants";
import { IAdjustmentResponse, IBidPricingResponse } from "@miplatform/types";
import { mdiChevronDown } from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
} from "@mui/material";
import {
  COLUMN_WIDTH,
  EMPTY_DRAW_ROW,
  IDrawRow,
  formatPrice,
  getColumns,
  getDrawValue,
  groupDrops,
} from "../utils/priceDetails";

export function PriceAdjustmentDetail({
  planDetails,
  priceDetails,
}: PriceAdjustmentDetailProps) {
  const { classes, cx } = useStyles();
  const { hasPermission } = usePermissions();
  const canReadPricing = hasPermission(PERMISSIONS.PRICING.READ);

  const details = planDetails ?? priceDetails;
  const planAreas = details?.planAreas ?? [];

  const columns = useMemo(() => getColumns(details), [details]);
  const dropsSummary = details?.dropsSummary ?? EMPTY_DRAW_ROW;
  const itemsSummary = details?.materialsSummary ?? EMPTY_DRAW_ROW;

  const renderDraws = (row: IDrawRow) => (
    <Grid container wrap="nowrap" spacing={1}>
      {columns.map((column) => {
        const value = getDrawValue(row, column);
        return (
          <Grid key={column} className={classes.columnWidth}>
            <Box className={classes.drawCell}>
              <Typography
                className={cx(classes.mediumText, classes.truncatedText)}
              >
                {column}
              </Typography>
              <Typography className={classes.mediumText}>
                {formatPrice(value ?? 0)}
              </Typography>
            </Box>
          </Grid>
        );
      })}
      <Grid className={classes.columnWidth}>
        <Box className={cx(classes.drawCell, classes.totalDrawCell)}>
          <Typography className={classes.mediumText}>Total</Typography>
          <Typography className={classes.mediumText}>
            {formatPrice(row.total)}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );

  const renderDrawValue = (
    row: IDrawRow,
    adjustmentVariant?: "positive" | "negative"
  ) => (
    <Grid container wrap="nowrap" spacing={1} className={classes.valueRow}>
      {columns.map((column) => {
        const value = getDrawValue(row, column);
        return (
          <Grid key={column} className={classes.columnWidth}>
            <Typography
              className={cx(
                classes.valueCell,
                adjustmentVariant === "positive" && classes.adjustmentValueCell,
                adjustmentVariant === "negative" &&
                  classes.adjustmentValueCellNegative
              )}
            >
              {formatPrice(value ?? 0)}
            </Typography>
          </Grid>
        );
      })}
      <Grid className={classes.columnWidth}>
        <Typography
          className={cx(
            classes.valueCell,
            adjustmentVariant === "positive" && classes.adjustmentValueCell,
            adjustmentVariant === "negative" &&
              classes.adjustmentValueCellNegative
          )}
        >
          {formatPrice(row.total ?? 0)}
        </Typography>
      </Grid>
    </Grid>
  );

  const isAdjustmentNegative = (adjustment: IAdjustmentResponse) =>
    (adjustment.total ?? adjustment.amount ?? 0) < 0;

  const renderAdjustmentValue = (adjustment: IAdjustmentResponse) =>
    renderDrawValue(
      {
        draws: adjustment.draws ?? [],
        total: adjustment.total ?? adjustment.amount ?? 0,
      },
      isAdjustmentNegative(adjustment) ? "negative" : "positive"
    );

  const renderGlobalAdjustment = (
    header: string,
    adjustments: IAdjustmentResponse[] = []
  ) => {
    if (!adjustments.length) return null;

    return (
      <Accordion
        defaultExpanded
        disableGutters
        className={classes.globalAdjustmentAccordion}
      >
        <AccordionSummary
          expandIcon={<MiIcon path={mdiChevronDown} />}
          className={cx(classes.areaHeader, classes.globalAdjustmentHeader)}
        >
          <Typography className={classes.mediumText}>{header}</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.globalAdjustmentBody}>
          {adjustments.map((adjustment) => {
            const isNegative = isAdjustmentNegative(adjustment);
            return (
              <Box
                key={adjustment.id}
                className={cx(classes.leafRow, classes.noBorderRow)}
              >
                <Typography
                  className={cx(
                    classes.adjustmentText,
                    isNegative && classes.adjustmentTextNegative
                  )}
                >
                  {adjustment.label}
                </Typography>
                {renderAdjustmentValue(adjustment)}
              </Box>
            );
          })}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderDropsSection = () => {
    return (
      <Accordion defaultExpanded disableGutters className={classes.accordion}>
        <AccordionSummary
          expandIcon={<MiIcon path={mdiChevronDown} />}
          className={classes.sectionHeader}
        >
          <Typography>Drops</Typography>
          <Box className={classes.drawsWrapper}>
            {renderDraws(dropsSummary)}
          </Box>
        </AccordionSummary>
        <AccordionDetails className={classes.sectionBody}>
          {(details?.globalDropAdjustment?.length ?? 0) > 0 && (
            <Box className={classes.nestedGlobalAdjustment}>
              {renderGlobalAdjustment(
                "Global Drop Adjustment",
                details?.globalDropAdjustment
              )}
            </Box>
          )}
          {planAreas.map((area) => {
            const areaDrops = area.materials.flatMap(
              (material) => material.drops ?? []
            );
            if (!areaDrops.length) return null;

            return (
              <Accordion
                key={area.planAreaId}
                defaultExpanded
                disableGutters
                className={classes.areaAccordion}
              >
                <AccordionSummary
                  expandIcon={<MiIcon path={mdiChevronDown} />}
                  className={classes.areaHeader}
                >
                  <Typography className={classes.mediumText}>
                    {area.planAreaLabel}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.areaBody}>
                  {groupDrops(areaDrops).map((drop) => {
                    const { adjustment } = drop;

                    return (
                      <Box key={drop.dropTypeId}>
                        <Box
                          className={cx(
                            classes.leafRow,
                            adjustment && classes.noBorderRow
                          )}
                        >
                          <Typography className={classes.highlightText}>
                            {`${drop.count}x ${drop.dropTypeName}`}
                          </Typography>
                          {renderDrawValue({
                            draws: drop.draws,
                            total: drop.total,
                          })}
                        </Box>
                        {adjustment && (
                          <Box
                            className={cx(
                              classes.leafRow,
                              classes.adjustmentRow
                            )}
                          >
                            <Typography
                              className={cx(
                                classes.adjustmentText,
                                isAdjustmentNegative(adjustment) &&
                                  classes.adjustmentTextNegative
                              )}
                            >
                              {adjustment.label}
                            </Typography>
                            {renderAdjustmentValue(adjustment)}
                          </Box>
                        )}
                        {drop.netTotal && (
                          <Box
                            className={cx(classes.leafRow, classes.netTotalRow)}
                          >
                            <Typography className={classes.netTotalText}>
                              Net Total
                            </Typography>
                            {renderDrawValue(drop.netTotal)}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderItemsSection = () => {
    return (
      <Accordion defaultExpanded disableGutters className={classes.accordion}>
        <AccordionSummary
          expandIcon={<MiIcon path={mdiChevronDown} />}
          className={classes.sectionHeader}
        >
          <Typography>Items</Typography>
          <Box className={classes.drawsWrapper}>
            {renderDraws(itemsSummary)}
          </Box>
        </AccordionSummary>
        <AccordionDetails className={classes.sectionBody}>
          {(details?.globalItemAdjustment?.length ?? 0) > 0 && (
            <Box className={classes.nestedGlobalAdjustment}>
              {renderGlobalAdjustment(
                "Global Item Adjustment",
                details?.globalItemAdjustment
              )}
            </Box>
          )}
          {planAreas.map((area) => {
            return (
              <Accordion
                key={area.planAreaId}
                defaultExpanded
                disableGutters
                className={classes.areaAccordion}
              >
                <AccordionSummary
                  expandIcon={<MiIcon path={mdiChevronDown} />}
                  className={classes.areaHeader}
                >
                  <Typography className={classes.mediumText}>
                    {area.planAreaLabel}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.areaBody}>
                  {area.materials.map((material) => {
                    const { adjustment } = material;

                    return (
                      <Box key={material.planMaterialId}>
                        <Box
                          className={cx(
                            classes.leafRow,
                            adjustment && classes.noBorderRow
                          )}
                        >
                          <Typography className={classes.highlightText}>
                            {`${material.quantity}x ${[
                              material.manufacturerPartNumber,
                              material.itemDescription,
                            ]
                              .filter(Boolean)
                              .join(" | ")}`}
                          </Typography>
                          {renderDrawValue(material)}
                        </Box>
                        {adjustment && (
                          <Box
                            className={cx(
                              classes.leafRow,
                              classes.adjustmentRow
                            )}
                          >
                            <Typography
                              className={cx(
                                classes.adjustmentText,
                                isAdjustmentNegative(adjustment) &&
                                  classes.adjustmentTextNegative
                              )}
                            >
                              {adjustment.label}
                            </Typography>
                            {renderAdjustmentValue(adjustment)}
                          </Box>
                        )}
                        {material.netTotal && (
                          <Box
                            className={cx(classes.leafRow, classes.netTotalRow)}
                          >
                            <Typography className={classes.netTotalText}>
                              Net Total
                            </Typography>
                            {renderDrawValue(material.netTotal)}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </AccordionDetails>
      </Accordion>
    );
  };

  if (!details) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <Accordion
        defaultExpanded
        disableGutters
        className={cx(classes.accordion, classes.drawsAccordion)}
      >
        <AccordionSummary
          expandIcon={canReadPricing && <MiIcon path={mdiChevronDown} />}
          className={cx(classes.sectionHeader, classes.drawsSectionHeader)}
        >
          <Typography>Bid</Typography>
          <Box className={classes.drawsWrapper}>{renderDraws(details)}</Box>
        </AccordionSummary>
        {canReadPricing && (
          <AccordionDetails className={classes.drawsSectionBody}>
            {renderGlobalAdjustment(
              "Bid Adjustment",
              details?.globalPlanAdjustment
            )}
            {planAreas.length > 0 && renderDropsSection()}
            {planAreas.length > 0 && renderItemsSection()}
          </AccordionDetails>
        )}
      </Accordion>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: 0,
    gap: theme.spacing(2),
    backgroundColor: theme.palette.grey.mediumLight,
    padding: theme.spacing(2),
  },
  accordion: {
    width: "100%",
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
    boxShadow: "none",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: 0,
    },
  },
  drawsAccordion: {
    borderColor: theme.palette.grey.accent,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 2),
    borderBottom: `1px solid ${theme.palette.grey.accent}`,
    backgroundColor: theme.palette.grey.light,
    minHeight: 48,
    "&.Mui-expanded": {
      minHeight: 48,
    },
    "& .MuiAccordionSummary-content": {
      display: "flex",
      alignItems: "center",
      margin: 0,
      "&.Mui-expanded": {
        margin: 0,
      },
    },
  },
  drawsSectionHeader: {
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.common.white}`,
    paddingRight: 16,
  },
  drawsSectionBody: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  drawsWrapper: {
    display: "flex",
    alignItems: "center",
    marginLeft: "auto",
  },
  drawCell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    borderRadius: 4,
    backgroundColor: theme.palette.primary.light,
    overflow: "hidden",
    padding: theme.spacing(0, 0.5),
  },
  totalDrawCell: {
    backgroundColor: theme.palette.green.light,
  },
  sectionBody: {
    display: "flex",
    flexDirection: "column",
    padding: 0,
  },
  areaAccordion: {
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    padding: theme.spacing(1, 2),
    backgroundColor: "transparent",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: 0,
    },
  },
  areaHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey.mediumLight,
    borderRadius: 4,
    minHeight: 32,
    "&.Mui-expanded": {
      minHeight: 32,
    },
    "& .MuiAccordionSummary-content": {
      margin: 0,
      "&.Mui-expanded": {
        margin: 0,
      },
    },
  },
  areaBody: {
    padding: 0,
  },
  globalAdjustmentAccordion: {
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    backgroundColor: "transparent",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: 0,
    },
  },
  nestedGlobalAdjustment: {
    padding: theme.spacing(1, 2),
  },
  globalAdjustmentHeader: {
    backgroundColor: theme.palette.blue.light,
  },
  globalAdjustmentBody: {
    padding: 0,
  },
  leafRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.grey.mediumLight}`,
  },
  valueRow: {
    flexShrink: 0,
    marginRight: 24,
  },
  valueCell: {
    boxSizing: "border-box",
    textAlign: "center",
    color: theme.palette.grey.medium,
    fontSize: theme.fontSize.medium,
  },
  mediumText: {
    fontSize: theme.fontSize.medium,
  },
  truncatedText: {
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  highlightText: {
    fontSize: theme.fontSize.medium,
    color: theme.palette.primary.main,
    fontWeight: theme.fontWeight.semiBold,
  },
  adjustmentRow: {
    paddingLeft: theme.spacing(3),
    borderBottom: "none",
  },
  adjustmentText: {
    fontSize: theme.fontSize.medium,
    color: theme.palette.green.main,
    fontWeight: theme.fontWeight.semiBold,
  },
  adjustmentValueCell: {
    color: theme.palette.green.main,
  },
  adjustmentTextNegative: {
    color: theme.palette.red.main,
  },
  adjustmentValueCellNegative: {
    color: theme.palette.red.main,
  },
  netTotalRow: {
    paddingLeft: theme.spacing(3),
    backgroundColor: theme.palette.yellow.lightAccent,
    borderBottom: "none",
  },
  netTotalText: {
    fontSize: theme.fontSize.medium,
    fontWeight: theme.fontWeight.semiBold,
    color: theme.palette.primary.main,
  },

  columnWidth: {
    width: COLUMN_WIDTH,
  },
  noBorderRow: {
    borderBottom: "none",
  },
}));

interface PriceAdjustmentDetailProps {
  planDetails?: IBidPricingResponse | null;
  priceDetails?: IBidPricingResponse | null;
}
